import {
  createContext,
  Dispatch,
  FC,
  memo,
  PropsWithChildren,
  ReactElement,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { generatePath, Link as ReactLink } from 'react-router';
import {
  Box,
  BoxProps,
  Flex,
  FlexProps,
  Link,
  LinkProps,
} from '@chakra-ui/react';

import { useOpenClose, UseOpenCloseReturn } from '~/core/hooks/useOpenClose';
import {
  useDocument,
  useDocumentCurrentStatus,
} from '~/core/shared/api/documents';
import { useApplicationDocumentStates } from '~/core/shared/api/middle-office/application';
import { SEOHead } from '~/core/shared/components';
import { FeatureKey, Module } from '~/core/shared/constants';
import { useTenantedApplicationConfig } from '~/core/shared/contexts';
import { useFeatures } from '~/core/shared/contexts/features';
import { useUserData } from '~/core/shared/contexts/UserContext';
import { Permissions } from '~/core/shared/generated/Permissions';
import { useRequiredParams } from '~/core/shared/hooks';
import { useCan } from '~/core/shared/hooks/useCan';
import { useDocumentNotificationNotification } from '~/core/shared/hooks/useDocumentNotificationNotification';
import { useHooksManager } from '~/core/shared/hooks/useHooksManager';
import { UseHooksManager } from '~/core/shared/hooks/useHooksManager/types';
import { useIndividualCreditEnquiryNotification } from '~/core/shared/hooks/useIndividualCreditEnquiryNotification/useIndividualCreditEnquiryNotification';
import { useUserMessageNotification } from '~/core/shared/hooks/useUserMessageNotification/useUserMessageNotification';
import {
  AnyDocument,
  DocumentStateResource,
  DocumentStatusResource,
} from '~/core/shared/types';
import { guardPasses } from '~/core/shared/utils/admin';
import {
  buildComponent,
  isLockedApplication,
} from '~/core/shared/utils/general';
import { useSubscribeToDocumentStatusUpdated } from '~/core/shared/websocketApi';
import {
  TenantDocumentDetailsViewAction,
  TenantDocumentViewRoute,
} from '../../config';
import {
  getDocumentTitleForDisplayData,
  useAdminApplicationContext,
} from '../../contexts/AdminApplicationContext';
import { AppBanner } from '../AppBanner.component';
import { Layout, LayoutProps } from '../Layout.component';
import { ApplicationSubheader } from './subheaders/Application.component';
import { DocumentDetailsViewContext } from './Details.view';

const defaultDisplayNameBuilder = (data: Record<string, any>): string => {
  const { businessEntityName, business, shortId } = data;
  return businessEntityName || business?.name || shortId;
};

type DocumentDetailsViewActionContext = {
  action: TenantDocumentDetailsViewAction;
};

type ApplicationLayoutProps = {
  selectedTabNavItem: string;
  sidebar?: ReactElement | undefined;
  contentBoxProps?: BoxProps;
} & Omit<LayoutProps, 'subheader' | 'selectedNavItem'>;

type NavItem = {
  key: string;
  numericalIndicator?: number;
  component?: ReactElement;
  path: (uuid: string) => string;
  selected?: boolean;
  text: string;
};

type ApplicationContextType = {
  applicationUuid: string;
  lastApplicationUpdate: Date;
  setLastApplicationUpdate: Dispatch<SetStateAction<Date>>;
  actionContexts: Record<string, Record<string, any>>;
  status: DocumentStatusResource | undefined;
  setStatus: Dispatch<SetStateAction<DocumentStatusResource | undefined>>;
  equifaxModalApi: UseOpenCloseReturn;
  definition: string;
  hooks: UseHooksManager['hooks'];
  fetchHooks: UseHooksManager['fetchHooks'];
  clearHooks: UseHooksManager['clearHooks'];
};

const getApplicationPath = (uuid: string, path: string): string =>
  generatePath(path, {
    uuid,
  });

const navigateToTenantDocumentViewRoute = (
  route: TenantDocumentViewRoute,
  uuid: string,
): string => getApplicationPath(uuid, route.path);

export const ApplicationContext = createContext<ApplicationContextType>(
  {} as ApplicationContextType,
);
export const DocumentDetailsViewActionContext =
  createContext<DocumentDetailsViewActionContext>(
    {} as DocumentDetailsViewActionContext,
  );

const DocumentDetailsViewActionProviderComponent: FC<
  PropsWithChildren<{ action: TenantDocumentDetailsViewAction }>
> = ({ action, children }) => {
  const value = useMemo(() => ({ action }), [action]);
  return (
    <DocumentDetailsViewActionContext.Provider value={value}>
      {children}
    </DocumentDetailsViewActionContext.Provider>
  );
};
export const DocumentDetailsViewActionProvider = memo(
  DocumentDetailsViewActionProviderComponent,
);

const sharedNavItemProps: LinkProps = {
  _hover: {
    textDecoration: 'none',
  },
  _focus: {
    focusRing: 'none',
  },
  borderBottomStyle: 'solid',
  borderBottomWidth: 3,
  color: 'black',
  display: 'flex',
  px: 4,
  py: 2,
  textDecoration: 'none',
};

const NavItemPossibleLink: FC<
  PropsWithChildren<{ item: NavItem; uuid: string; definition: string }>
> = ({ children, item, uuid }) => {
  if (item.selected) {
    return (
      <Flex
        {...(sharedNavItemProps as FlexProps)}
        alignItems="start"
        borderBottomColor="gray.100"
        fontWeight="bold"
      >
        {children}
      </Flex>
    );
  }
  return (
    <Link
      {...sharedNavItemProps}
      alignItems="start"
      asChild
      borderBottomColor="white"
      display="flex"
      fontWeight="normal"
    >
      <ReactLink to={item.path(uuid)}>{children}</ReactLink>
    </Link>
  );
};

// @TODO: pass application to subheader
export const ApplicationLayout: FC<ApplicationLayoutProps> = ({
  children,
  selectedTabNavItem,
  sidebar = false,
  contentBoxProps,
  ...layoutProps
}) => {
  const { getFeature } = useFeatures();
  const config = useTenantedApplicationConfig();
  const { displayData, setDisplayData } = useAdminApplicationContext();
  const { user } = useUserData();
  const { can, meetsRequirement } = useCan();
  const [status, setStatus] = useState<DocumentStatusResource>();
  const [lastApplicationUpdate, setLastApplicationUpdate] = useState<Date>(
    new Date(),
  );
  const { uuid } = useRequiredParams(['uuid']);
  const { config: documentDetailsViewConfig } = useContext(
    DocumentDetailsViewContext,
  );
  const { tabs, actions } = documentDetailsViewConfig;
  const tenantDocumentDefinition =
    documentDetailsViewConfig.documentRouteProps?.tenantDocumentDefinition ??
    '';
  const { modules } = config.documents[tenantDocumentDefinition];
  const { statusDefinition } = modules[Module.MiddleOffice].primaryWorkflow;

  const navItems = useMemo(() => {
    const filteredNavItems: NavItem[] = [];

    tabs.forEach((tab) => {
      if (tab.hidden) {
        return;
      }

      if (tab.route.permission && !meetsRequirement(tab.route.permission)) {
        return;
      }

      const tabFeature = tab.route.feature
        ? getFeature(tab.route.feature)
        : undefined;

      if (tabFeature && !tabFeature.enabled) {
        return;
      }

      const tabNavItem = {
        component: tab.tabItem
          ? buildComponent(tab.tabItem, {
              uuid,
              definition: tenantDocumentDefinition,
            })
          : undefined,
        key: tab.key,
        path: (pathUuid: string) =>
          navigateToTenantDocumentViewRoute(tab.route, pathUuid),
        selected: tab.key === selectedTabNavItem,
        text: tab.text,
      };
      filteredNavItems.push(tabNavItem);
    });

    return filteredNavItems;
  }, [selectedTabNavItem, tabs, user]);

  const equifaxModalApi = useOpenClose();

  const { documentStates } = useApplicationDocumentStates(
    uuid,
    tenantDocumentDefinition,
    [modules[Module.MiddleOffice].primaryWorkflow.workflowDefinition],
  );

  const actionComponents: Record<string, ReactElement | undefined> = {};
  const actionContexts: Record<string, Record<string, any>> = {};
  const actionsByKey: Record<string, TenantDocumentDetailsViewAction> = {};
  // @TODO Fix weak association. The names of workflow and status definitions may not be the same.
  const getApplicationStatus = (): DocumentStateResource | undefined =>
    documentStates?.find((s) => s.workflow === statusDefinition);

  const enabledActionFeatures = useMemo(
    () =>
      actions.reduce<Array<FeatureKey | string>>((features, action) => {
        if (!action.feature) {
          return features;
        }

        const actionFeature = getFeature(action.feature);

        if (actionFeature?.enabled) {
          features.push(action.feature);
        }

        return features;
      }, []),
    [actions],
  );

  actions.forEach((action) => {
    if (action.feature && !enabledActionFeatures.includes(action.feature)) {
      return;
    }
    if (action.contentComponent) {
      actionComponents[action.key] = buildComponent(action.contentComponent);
    }
    if (action.context) {
      actionContexts[action.key] = action.context();
    }
    if (
      (action.permission && !meetsRequirement(action.permission)) ||
      !can(Permissions.ActionsView)
    ) {
      return;
    }

    if (action.guard && !guardPasses(action.guard, displayData)) {
      return;
    }

    actionsByKey[action.key] = action;
  });

  const { currentStatus } = useDocumentCurrentStatus(
    uuid,
    tenantDocumentDefinition,
    modules[Module.MiddleOffice].primaryWorkflow.statusDefinition,
  );

  useDocumentNotificationNotification(uuid);
  // TODO: Remove Nullable from store User when accessing authenticated parts of the website
  useUserMessageNotification(user?.id || '');

  useIndividualCreditEnquiryNotification(user?.id || '', () =>
    setLastApplicationUpdate(new Date()),
  );

  useSubscribeToDocumentStatusUpdated(uuid, ({ status: documentStatus }) => {
    setStatus(documentStatus);
  });

  const { hooks, fetchHooks, clearHooks } = useHooksManager();

  useEffect(() => {
    if (currentStatus) {
      setStatus(currentStatus);
    }
  }, [currentStatus]);

  const includes = documentDetailsViewConfig.meta?.includes ?? [];
  const displayNameBuilderFn =
    documentDetailsViewConfig.meta?.displayName ?? defaultDisplayNameBuilder;

  const { document: applicationRootDocument } = useDocument(
    uuid,
    tenantDocumentDefinition,
    includes,
  );

  useEffect(() => {
    if (!applicationRootDocument) return;
    const appData = applicationRootDocument as unknown as Record<string, any>;
    const { shortId, allowCopy } = appData;
    const workflowDisplayName =
      config
        .getWorkflowConfig()
        ?.defaultWorkflow?.split('_')
        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ') ?? '';
    setDisplayData({
      entityDisplayName: displayNameBuilderFn(appData),
      entityShortId: shortId,
      entityAllowCopy: allowCopy,
      statusDisplayName: 'Open',
      workflowDisplayName,
    });
  }, [applicationRootDocument]);

  useEffect(() => {
    if (status && displayData) {
      setDisplayData({ ...displayData, statusDisplayName: status.title || '' });
    }
    fetchHooks({
      id: uuid,
      definition: tenantDocumentDefinition,
    } as AnyDocument);
  }, [status]);

  useEffect(() => {
    if (
      documentStates &&
      documentStates.length > 0 &&
      displayData &&
      displayData.statusDisplayName === ''
    ) {
      setDisplayData({
        ...displayData,
        statusDisplayName: getApplicationStatus()?.place?.meta?.title || '',
      });
    }
  }, [documentStates]);

  const renderTabsHeader = (): ReactElement => (
    <Flex borderBottom="2px solid" borderBottomColor="gray.100" mb={6}>
      {navItems.map((item) => (
        <NavItemPossibleLink
          key={`${tenantDocumentDefinition}.${uuid}.${item.key}`}
          definition={tenantDocumentDefinition}
          item={item}
          uuid={uuid}
        >
          {item.component}

          {!item.component && (
            <>
              <Box as="span">{item.text}</Box>

              {item.numericalIndicator && item.numericalIndicator > 0 && (
                <Flex
                  alignItems="center"
                  as="span"
                  bg="mint.500"
                  borderRadius="full"
                  boxSize={6}
                  fontWeight="bold"
                  justifyContent="center"
                  ml={1.5}
                >
                  {item.numericalIndicator}
                </Flex>
              )}
            </>
          )}
        </NavItemPossibleLink>
      ))}
    </Flex>
  );

  const sidebarRef = useRef<HTMLDivElement>(null);

  const isLocked = isLockedApplication(hooks);

  const applicationContextValue = useMemo(
    () => ({
      actionContexts,
      applicationUuid: uuid,
      definition: tenantDocumentDefinition,
      lastApplicationUpdate,
      setLastApplicationUpdate,
      setStatus,
      status,
      equifaxModalApi,
      hooks,
      fetchHooks,
      clearHooks,
    }),
    [
      actionContexts,
      uuid,
      tenantDocumentDefinition,
      lastApplicationUpdate,
      setLastApplicationUpdate,
      setStatus,
      status,
      equifaxModalApi,
      hooks,
      fetchHooks,
      clearHooks,
    ],
  );

  return (
    <ApplicationContext.Provider value={applicationContextValue}>
      <SEOHead title={getDocumentTitleForDisplayData(displayData)} />
      <Layout
        {...layoutProps}
        selectedNavItem={documentDetailsViewConfig.nav ?? 'applications'}
        subheader={
          <>
            {isLocked && (
              <AppBanner
                message="Application is locked and unable to be edited"
                status="error"
              />
            )}
            <ApplicationSubheader />
            {/* <Box bgColor="white" pl={sidebarRef.current?.offsetWidth ? sidebarRef.current?.offsetWidth + 48 + 'px' : 12} pt="4"> */}
            <Box bgColor="white" pl="12" pt="4">
              {renderTabsHeader()}
            </Box>
          </>
        }
      >
        {Object.entries(actionComponents).map((entry) => {
          const action = actionsByKey[entry[0]];
          const actionComponent = entry[1];
          if (action && actionComponent) {
            return (
              <DocumentDetailsViewActionProvider
                key={action.key}
                action={action}
              >
                {actionComponent}
              </DocumentDetailsViewActionProvider>
            );
          }
          return null;
        })}
        <Flex direction="row">
          <Box
            ref={sidebarRef}
            borderColor="gray.200"
            borderLeft="1 solid"
            ml="12"
          >
            {sidebar}
          </Box>
          <Box
            flex="1"
            {...{
              ...{
                bgColor: 'white',
                pb: 12,
                pt: 6,
                px: 12,
              },
              ...(contentBoxProps || {}),
            }}
          >
            {children}
          </Box>
        </Flex>
      </Layout>
    </ApplicationContext.Provider>
  );
};
